import { api, throwApiError } from "../../api/api";

/**
 * Como o enum vem do Prisma, eu coloco os valores conhecidos + string
 * pra não quebrar caso você adicione novos status no futuro.
 */
export type SlotRegistrationStatus =
  | "PENDENTE"
  | "APROVADO"
  | "REJEITADO"
  | "CANCELADO"
  | "FALTOU"
  | "AUSENTE"
  | string;

// -------------------------
// Helpers
// -------------------------
const cleanParams = <T extends Record<string, any>>(params: T): Partial<T> => {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") (out as any)[k] = v;
  }
  return out;
};

// -------------------------
// Types - Slots
// -------------------------
export type CreateSlotInput = {
  day: string; // YYYY-MM-DD
  startAt: string; // ISO
  endAt: string; // ISO
  capacity: number;
};

export type CreateSlotsDto = {
  slots: CreateSlotInput[];
};

export type UpdateSlotDto = {
  day?: string;
  startAt?: string;
  endAt?: string;
  capacity?: number;
};

export type ListSlotsQuery = {
  day?: string; // YYYY-MM-DD
  from?: string; // ISO date-time
  to?: string; // ISO date-time
  onlyAvailable?: boolean;
  includeCanceled?: boolean;
};

export type StoreSlot = {
  id: string;
  storeId: string;
  day: string; // ISO date (ou YYYY-MM-DD dependendo do serializer)
  startAt: string; // ISO
  endAt: string; // ISO
  capacity: number;
  extraCapacity?: number | null;
  isCanceled: boolean;
  createdAt: string; // ISO
  updatedAt: string; // ISO
};

export type StoreSlotWithAvailability = StoreSlot & {
  occupied: number;
  totalCapacity: number;
  available: number;
  isFull: boolean;
};

// -------------------------
// Types - Registrations
// -------------------------
export type CreateRegistrationResponse = {
  id: string;
  status: SlotRegistrationStatus;
  day: string;
  createdAt: string;
  slot: {
    id: string;
    storeId: string;
    day: string;
    startAt: string;
    endAt: string;
    store: {
      id: string;
      nomeFantasia: string;
    };
  };
};

export type MyRegistrationItem = {
  id: string;
  status: SlotRegistrationStatus;
  day: string;
  decidedAt?: string | null;
  createdAt: string;
  slot: {
    id: string;
    storeId: string;
    day: string;
    startAt: string;
    endAt: string;
    store: {
      id: string;
      nomeFantasia: string;
    };
  };
};

export type ListMyRegistrationsQuery = {
  from?: string;
  to?: string;
  status?: SlotRegistrationStatus;
};

export type CancelRegistrationResponse = {
  id: string;
  status: SlotRegistrationStatus;
  updatedAt: string;
};

export type StoreRegistrationItem = {
  id: string;
  status: SlotRegistrationStatus;
  day: string;
  createdAt: string;
  decidedAt?: string | null;
  decidedById?: string | null;
  vacancyOpenedForSlotId?: string | null;
  freelancer: {
    id: string;
    nomeCompleto: string;
    cpf: string;
    pix?: string | null;
    banco?: string | null;
  };
  slot: {
    id: string;
    storeId: string;
    day: string;
    startAt: string;
    endAt: string;
  };
  payment?: {
    totalValue: string;
  } | null;
};

export type ListStoreRegistrationsQuery = {
  startDay?: string;
  endDay?: string;
  status?: SlotRegistrationStatus;
};

export type UpdateRegistrationStatusDto = {
  status: SlotRegistrationStatus;
};

export type UpdateRegistrationStatusResponse = {
  id: string;
  status: SlotRegistrationStatus;
  decidedAt: string;
  decidedById: string;
  vacancyOpenedForSlotId?: string | null;
};

// -------------------------
// Types - Payments (Registration)
// -------------------------
export type RegistrationPayment = {
  id: string;
  registrationId: string;

  baseValue: string;
  baseHours: number;
  hourlyValue: string;

  extraHours: number;
  extraNote?: string | null;
  extraValue: string;

  earlyLeaveHours: number;
  earlyLeaveNote?: string | null;
  earlyLeaveValue: string;

  arbitraryValue: string;
  arbitraryNote?: string | null;

  totalValue: string;

  calculatedById?: string | null;
  calculatedAt: string;
  updatedAt: string;
};

export type FreelancerAfiliacao = "ECO" | "ZE";

export type UpdateRegistrationPaymentDto = {
  extraHours?: number;
  extraNote?: string;

  earlyLeaveHours?: number;
  earlyLeaveNote?: string;

  arbitraryValue?: string;
  arbitraryNote?: string;

  afiliacao?: FreelancerAfiliacao;
};

export type AdminCreateRegistrationsDto = {
  freelancerIds: string[];
};

export type AdminCreateRegistrationsResponse = {
  created: Array<{
    id: string;
    freelancerId: string;
    status: SlotRegistrationStatus;
    day: string;
    createdAt: string;
  }>;
  skipped: Array<{
    freelancerId: string;
    reason: string;
  }>;
};

// ======================================================================
// API - Slot Share Links (NEW)
// ======================================================================

export type CreateSlotShareLinkDto = {
  slotIds: string[];
};

export type CreateSlotShareLinkResponse = {
  token: string;
  expiresAt: string;
};

export type PublicSlotShareLinkInfo = {
  store: {
    id: string;
    nomeFantasia: string;
    endereco: string | null;
  };
  expiresAt: string;
};

export type CreateSlotForSubstitutionDto = {
  startAt: string;
  endAt: string;
};

export type SubstituteRegistrationDto = {
  newFreelancerId: string;
  targetSlotId?: string;
  createSlot?: CreateSlotForSubstitutionDto;
};

export type SubstituteRegistrationResponse = {
  canceledOld: {
    id: string;
    status: SlotRegistrationStatus;
    freelancerId: string;
    decidedAt: string;
    decidedById: string;
  };
  createdNew: {
    id: string;
    status: SlotRegistrationStatus;
    day: string;
    createdAt: string;
    freelancerId: string;
    slot: {
      id: string;
      storeId: string;
      day: string;
      startAt: string;
      endAt: string;
    };
  };
  targetSlot: {
    id: string;
    storeId: string;
    day: string;
    startAt: string;
    endAt: string;
    capacity: number;
    extraCapacity: number;
    isCanceled: boolean;
  };
};

// ======================================================================
// API - Slots
// ======================================================================

/**
 * POST /stores/:storeId/slots
 */
export const createSlotsApi = async (
  storeId: string,
  dto: CreateSlotsDto
): Promise<StoreSlot[]> => {
  try {
    const { data } = await api.post<StoreSlot[]>(
      `/stores/${storeId}/slots`,
      dto
    );
    return data;
  } catch (error: any) {
    const data = error?.response?.data;
    const msg =
      Array.isArray(data?.message)
        ? data.message.join(", ")
        : (data?.message ?? error?.message ?? "Erro ao criar horários (slots).");

    throw new Error(msg);
  }
};

export const updateSlotApi = async (
  storeId: string,
  slotId: string,
  dto: UpdateSlotDto
): Promise<StoreSlot> => {
  try {
    const { data } = await api.patch<StoreSlot>(
      `/stores/${storeId}/slots/${slotId}`,
      dto
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao atualizar o slot.");
    throw error;
  }
};

export const deleteSlotApi = async (
  storeId: string,
  slotId: string
): Promise<StoreSlot> => {
  try {
    const { data } = await api.delete<StoreSlot>(
      `/stores/${storeId}/slots/${slotId}`
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao excluir o slot.");
    throw error;
  }
};

/**
 * GET /stores/:storeId/slots?day=...&from=...&to=...&onlyAvailable=...&includeCanceled=...
 */
export const listStoreSlotsApi = async (
  storeId: string,
  query: ListSlotsQuery = {}
): Promise<StoreSlotWithAvailability[]> => {
  try {
    const { data } = await api.get<StoreSlotWithAvailability[]>(
      `/stores/${storeId}/slots`,
      { params: cleanParams(query) }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao listar slots da loja.");
    throw error;
  }
};

/**
 * GET /stores/:storeId/slots/:slotId
 */
export const getStoreSlotApi = async (
  storeId: string,
  slotId: string
): Promise<StoreSlotWithAvailability> => {
  try {
    const { data } = await api.get<StoreSlotWithAvailability>(
      `/stores/${storeId}/slots/${slotId}`
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao buscar detalhes do slot.");
    throw error;
  }
};

// ======================================================================
// API - Registrations
// ======================================================================

/**
 * POST /slots/:slotId/registrations
 */
export const createRegistrationApi = async (
  slotId: string
): Promise<CreateRegistrationResponse> => {
  try {
    const { data } = await api.post<CreateRegistrationResponse>(
      `/slots/${slotId}/registrations`
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao criar inscrição no slot.");
    throw error;
  }
};

/**
 * GET /me/registrations?from=...&to=...&status=...
 */
export const listMyRegistrationsApi = async (
  token: string,
  query: ListMyRegistrationsQuery = {}
): Promise<MyRegistrationItem[]> => {
  try {
    const { data } = await api.get<MyRegistrationItem[]>(
      `/me/registrations`,
      {
        params: cleanParams(query),
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao listar minhas inscrições.");
    throw error;
  }
};

/**
 * DELETE /registrations/:registrationId
 */
export const cancelRegistrationApi = async (
  registrationId: string
): Promise<CancelRegistrationResponse> => {
  try {
    const { data } = await api.delete<CancelRegistrationResponse>(
      `/registrations/${registrationId}`
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao cancelar inscrição.");
    throw error;
  }
};

/**
 * GET /stores/:storeId/registrations?day=YYYY-MM-DD&status=PENDENTE
 */
export const listStoreRegistrationsApi = async (
  storeId: string,
  token: string,
  query: ListStoreRegistrationsQuery = {}
): Promise<StoreRegistrationItem[]> => {
  try {
    const { data } = await api.get<StoreRegistrationItem[]>(
      `/stores/${storeId}/registrations`,
      {
        params: cleanParams(query),
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao listar inscrições da loja.");
    throw error;
  }
};

/**
 * PATCH /registrations/:registrationId/status
 */
export const updateRegistrationStatusApi = async (
  registrationId: string,
  token: string,
  dto: UpdateRegistrationStatusDto
): Promise<UpdateRegistrationStatusResponse> => {
  try {
    const { data } = await api.patch<UpdateRegistrationStatusResponse>(
      `/registrations/${registrationId}/status`,
      dto,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao atualizar status da inscrição.");
    throw error;
  }
};

export const listStoreSlotsWithTokenApi = async (
  storeId: string,
  token: string,
  query: ListSlotsQuery = {}
): Promise<StoreSlotWithAvailability[]> => {
  try {
    const { data } = await api.get<StoreSlotWithAvailability[]>(
      `/stores/${storeId}/slots`,
      {
        params: cleanParams(query),
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao listar slots da loja.");
    throw error;
  }
};

export const createRegistrationWithTokenApi = async (
  slotId: string,
  token: string
): Promise<CreateRegistrationResponse> => {
  try {
    const { data } = await api.post<CreateRegistrationResponse>(
      `/slots/${slotId}/registrations`,
      undefined,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao criar inscrição no slot.");
    throw error;
  }
};

export const getRegistrationPaymentApi = async (
  registrationId: string,
  token: string
): Promise<RegistrationPayment> => {
  try {
    const { data } = await api.get<RegistrationPayment>(
      `/registrations/${registrationId}/payment`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao buscar valores da inscrição.");
    throw error;
  }
};

export const updateRegistrationPaymentApi = async (
  registrationId: string,
  token: string,
  dto: UpdateRegistrationPaymentDto
): Promise<RegistrationPayment> => {
  try {
    const { data } = await api.patch<RegistrationPayment>(
      `/registrations/${registrationId}/payment`,
      dto,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao ajustar valores da inscrição.");
    throw error;
  }
};

export const adminCreateRegistrationsApi = async (
  slotId: string,
  token: string,
  dto: AdminCreateRegistrationsDto
): Promise<AdminCreateRegistrationsResponse> => {
  try {
    const { data } = await api.post<AdminCreateRegistrationsResponse>(
      `/slots/${slotId}/registrations/admin`,
      dto,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao cadastrar freelancers no slot.");
    throw error;
  }
};

/**
 * POST /stores/:storeId/slot-share-links (ADMIN)
 */
export const createSlotShareLinkApi = async (
  storeId: string,
  token: string,
  dto: CreateSlotShareLinkDto
): Promise<CreateSlotShareLinkResponse> => {
  try {
    const { data } = await api.post<CreateSlotShareLinkResponse>(
      `/stores/${storeId}/slot-share-links`,
      dto,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return data;
  } catch (error: any) {
    const data = error?.response?.data;
    const msg =
      Array.isArray(data?.message)
        ? data.message.join(", ")
        : (data?.message ?? error?.message ?? "Erro ao gerar link de compartilhamento.");
    throw new Error(msg);
  }
};

/**
 * GET /public/slot-share-links/:token  (PÚBLICO)
 */
export const getPublicSlotShareLinkInfoApi = async (
  shareToken: string
): Promise<PublicSlotShareLinkInfo> => {
  try {
    const { data } = await api.get<PublicSlotShareLinkInfo>(
      `/public/slot-share-links/${shareToken}`
    );
    return data;
  } catch (error: any) {
    // se backend retornar 410, a mensagem vem em response.data.message
    const data = error?.response?.data;
    const msg =
      Array.isArray(data?.message)
        ? data.message.join(", ")
        : (data?.message ?? error?.message ?? "Link inválido ou expirado.");
    throw new Error(msg);
  }
};

/**
 * GET /public/slot-share-links/:token/slots?...
 */
export const listSlotsByShareTokenApi = async (
  shareToken: string,
  query: ListSlotsQuery = {}
): Promise<StoreSlotWithAvailability[]> => {
  try {
    const { data } = await api.get<StoreSlotWithAvailability[]>(
      `/public/slot-share-links/${shareToken}/slots`,
      { params: cleanParams(query) }
    );
    return data;
  } catch (error: any) {
    const data = error?.response?.data;
    const msg =
      Array.isArray(data?.message)
        ? data.message.join(", ")
        : (data?.message ?? error?.message ?? "Erro ao listar horários via link.");
    throw new Error(msg);
  }
};

export const substituteRegistrationApi = async (
  registrationId: string,
  token: string,
  dto: SubstituteRegistrationDto
): Promise<SubstituteRegistrationResponse> => {
  try {
    const { data } = await api.patch<SubstituteRegistrationResponse>(
      `/registrations/${registrationId}/substitute`,
      dto,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao substituir freelancer na inscrição.");
    throw error;
  }
};

export const listAllSlotsApi = async (params: {
  from: string;
  to: string;
  includeCanceled?: boolean;
  onlyAvailable?: boolean;
}): Promise<StoreSlotWithAvailability[]> => {
  const { data } = await api.get<StoreSlotWithAvailability[]>("/slots", {
    params,
  });
  return data;
};

export const listAllRegistrationsApi = async (
  token: string,
  params: {
    startDay: string;
    endDay: string;
  }
): Promise<any[]> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;

  const { data } = await api.get<any[]>("/registrations", {
    params,
    headers: { Authorization: `Bearer ${rawToken}` },
  });

  return data;
};

